

import {
  CheckCircle as CheckCircleIcon,
  AutoAwesome as SparklesIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StyleChangeConfirmationDialog } from './style-change-confirmation-dialog';


import type { Novel } from '@/lib/types';

import { SecureImage } from '@/components/secure-image';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useGenerateStylePreview } from '@/hooks/use-generate-style-preview';
import { useResponsiveValue } from '@/hooks/use-responsive-value';

type StyleOption = {
  id: 'style1' | 'style2' | 'style3' | 'style4' | 'style5' | 'style6' | 'custom';
  name: string;
  description: string;
};

const styleOptions: StyleOption[] = [
  { id: 'style1', name: 'dashboard.image_style.style1_name', description: 'dashboard.image_style.style1_desc' },
  { id: 'style2', name: 'dashboard.image_style.style2_name', description: 'dashboard.image_style.style2_desc' },
  { id: 'style3', name: 'dashboard.image_style.style3_name', description: 'dashboard.image_style.style3_desc' },
  { id: 'style4', name: 'dashboard.image_style.style4_name', description: 'dashboard.image_style.style4_desc' },
  { id: 'style5', name: 'dashboard.image_style.style5_name', description: 'dashboard.image_style.style5_desc' },
  { id: 'style6', name: 'dashboard.image_style.style6_name', description: 'dashboard.image_style.style6_desc' },
  { id: 'custom', name: 'dashboard.image_style.custom_name', description: 'dashboard.image_style.custom_desc' },
];

const LOCAL_STYLE_IMAGES: Record<string, string> = {
  style1: '/images/style-images/photorealistic.png',
  style2: '/images/style-images/western.png',
  style3: '/images/style-images/manga.png',
  style4: '/images/style-images/cinematic.png',
  style5: '/images/style-images/illustration_design.png',
  style6: '/images/style-images/children_animation.png',
};

export function ImageStyleSection() {
  const { novel } = useNovel();
  if (!novel.id) return null;

  const { t } = useTranslation();
  const { backend } = useFirebase();
  const { generatePreview, loading: generating } = useGenerateStylePreview();
  const resolveResponsive = useResponsiveValue();

  const [customStyle, setCustomStyle] = useState(novel.customStylePrompt || '');
  const [tempPreview, setTempPreview] = useState<{ url: string; path: string } | null>(null);
  const [hasTriedCustom, setHasTriedCustom] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingStyle, setPendingStyle] = useState<string | null>(null);

  const currentStyle = novel.imageStyle || 'style1';

  const handleTryOut = async () => {
    if (!customStyle.trim()) return;
    const result = await generatePreview(novel.id, customStyle);
    if (result) {
      setTempPreview({ url: result.imageUrl, path: result.storagePath });
      setHasTriedCustom(true);
    }
  };

  const handleStyleSelect = (styleId: string) => {
    // For custom style, user must try it out first
    if (styleId === 'custom' && !hasTriedCustom) {
      return;
    }

    // Don't confirm if selecting the same style
    if (styleId === currentStyle) return;

    setPendingStyle(styleId);
    setConfirmDialogOpen(true);
  };

  const handleConfirmStyleChange = async () => {
    if (!backend || !pendingStyle) return;

    try {
      const updateData: Partial<Novel> = {
        imageStyle: pendingStyle as Novel['imageStyle'],
      };

      if (pendingStyle === 'custom') {
        updateData.customStylePrompt = customStyle;
        if (tempPreview) {
          updateData.customStylePreviewUrl = tempPreview.url;
          updateData.customStylePreviewPath = tempPreview.path;
        }
      }

      await backend.novels.updateNovel(novel.ownerId, novel.id, updateData);
      setConfirmDialogOpen(false);
      setPendingStyle(null);
    } catch (error) {
      console.error('Error updating image style:', error);
    }
  };

  const getImageData = (styleId: string): { url: string; path?: string } => {
    if (styleId === 'custom') {
      return {
        url: tempPreview?.url || novel.customStylePreviewUrl || LOCAL_STYLE_IMAGES.style4,
        path: tempPreview?.path || novel.customStylePreviewPath
      };
    }
    return { url: LOCAL_STYLE_IMAGES[styleId] };
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {t('dashboard.image_style.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('dashboard.image_style.description')}
        </Typography>

        <Grid container spacing={2}>
          {styleOptions.map((style) => {
            const isSelected = currentStyle === style.id;
            const imageData = getImageData(style.id);
            const isCustom = style.id === 'custom';
            const canSelect = !isCustom || hasTriedCustom;

            return (
              <Grid
                key={style.id}
                size={isCustom ? { xs: 12, sm: 12, md: 6 } : { xs: 12, sm: 6, md: 3 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    border: '2px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: !isSelected && canSelect ? 'translateY(-4px)' : 'none',
                      boxShadow: !isSelected && canSelect ? 4 : 'none',
                    }
                  }}
                >
                  {isCustom ? (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexGrow: 1 }}>
                      {/* Left: Selection area (mirrors other cards) */}
                      <CardActionArea
                        onClick={() => handleStyleSelect(style.id)}
                        disabled={!canSelect}
                        sx={{
                          flex: { sm: '0 0 calc(50% - 8px)' }, // Perfectly match standard card width
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          borderRight: { sm: '1px solid' },
                          borderColor: { sm: 'divider' },
                        }}
                      >
                        {isSelected && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              bgcolor: 'primary.main',
                              borderRadius: '50%',
                              p: 0.5,
                              boxShadow: 2
                            }}
                          >
                            <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                          </Box>
                        )}
                        {imageData.url && (
                          <SecureImage
                            src={imageData.url}
                            path={imageData.path}
                            alt={t(style.name)}
                            sx={{
                              width: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <CardContent sx={{ p: 1.5, flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                            {t(style.name)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t(style.description)}
                          </Typography>
                        </CardContent>
                      </CardActionArea>

                      {/* Right: Prompt area (Maximum height) */}
                      <Box sx={{
                        flex: { sm: '0 0 calc(50% + 8px)' },
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { sm: 220 } // Ensure enough height for the custom UX
                      }}>
                        <TextField
                          fullWidth
                          multiline
                          placeholder={t('dashboard.image_style.placeholder')}
                          value={customStyle}
                          onChange={(e) => setCustomStyle(e.target.value)}
                          size="small"
                          sx={{
                            mb: 1.5,
                            flexGrow: 1,
                            '& .MuiInputBase-root': {
                              height: '100%',
                              alignItems: 'flex-start',
                              py: 1,
                              bgcolor: 'action.hover'
                            },
                            '& .MuiInputBase-input': {
                              height: '100% !important'
                            }
                          }}
                          InputProps={{ sx: { fontSize: '0.85rem' } }}
                        />
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          color="secondary"
                          startIcon={
                            generating ? <CircularProgress size={14} color="inherit" /> : <SparklesIcon />
                          }
                          onClick={handleTryOut}
                          disabled={!customStyle.trim() || generating}
                          sx={{ borderRadius: 1.5, fontSize: '0.75rem', py: 0.75 }}
                        >
                          {generating ? t('common.loading') : t('dashboard.image_style.try_out')}
                        </Button>
                        {!hasTriedCustom && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1, fontSize: '0.65rem', lineHeight: 1.2, textAlign: 'center' }}
                          >
                            {t('dashboard.image_style.try_it_first')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <CardActionArea
                      onClick={() => handleStyleSelect(style.id)}
                      disabled={!canSelect}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        flexGrow: 1,
                        opacity: !canSelect ? 0.6 : 1,
                      }}
                    >
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            p: 0.5,
                            boxShadow: 2
                          }}
                        >
                          <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                      )}

                      {imageData.url && (
                        <SecureImage
                          src={imageData.url}
                          path={imageData.path}
                          alt={t(style.name)}
                          sx={{
                            width: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      )}

                      <CardContent sx={{ flexGrow: 1, width: '100%', p: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.25 }}>
                          {t(style.name)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(style.description)}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <StyleChangeConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingStyle(null);
        }}
        onConfirm={handleConfirmStyleChange}
        currentStyle={currentStyle}
        newStyle={pendingStyle || ''}
      />
    </>
  );
}
